/**
 * Gate 4 · Slice 4.6 · Asset Sets Block
 *
 * Groups a picked text variant + image variant + branded preview into
 * a shippable bundle. Status flow: draft → review_ready → approved →
 * published. Approving freezes the referenced variants.
 */
import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const STATUS_LABEL = {
  draft: "DRAFT",
  review_ready: "READY FOR REVIEW",
  approved: "APPROVED · FROZEN",
  published: "PUBLISHED",
};

const NEXT_STATUS = {
  draft: "review_ready",
  review_ready: "approved",
  approved: "published",
  published: null,
};

function apiErr(e) {
  return e?.response?.data?.detail?.reason || e?.response?.data?.detail || e?.message || "error";
}

export function AssetSetsBlock({ assetGroupId, groupIndex, textVersion, imageVersions, backendUrl }) {
  const [sets, setSets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");

  const reload = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/creative/asset-groups/${assetGroupId}/asset-sets`);
      setSets(r.data?.asset_sets || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, [assetGroupId]);

  const createSet = async () => {
    setCreating(true);
    try {
      await api.post(`/creative/asset-sets`, {
        asset_group_id: assetGroupId,
        name: name.trim() || `Asset set ${sets.length + 1}`,
      });
      toast.success("Asset set created (draft)");
      setName("");
      await reload();
    } catch (e) {
      toast.error("Create failed", { description: apiErr(e) });
    } finally {
      setCreating(false);
    }
  };

  return (
    <div
      data-testid={`camp-creative-group-${groupIndex}-asset-sets`}
      style={{
        marginTop: 14, border: "1px solid var(--bo-line)",
        borderRadius: 6, background: "var(--bo-panel)",
      }}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="bo-btn"
        data-testid={`camp-creative-group-${groupIndex}-asset-sets-toggle`}
        style={{
          width: "100%", textAlign: "left", padding: "8px 12px",
          fontSize: 12.5, background: "transparent", border: "none",
          borderBottom: expanded ? "1px solid var(--bo-line)" : "none",
          fontFamily: "var(--bo-mono)", color: "var(--bo-fg)", cursor: "pointer",
        }}
      >
        {expanded ? "▾" : "▸"}  ASSET SETS  ·  {sets.length} bundle{sets.length === 1 ? "" : "s"}
      </button>
      {expanded && (
        <div style={{ padding: 12, display: "grid", gap: 10 }}>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Name for new asset set"
              data-testid={`camp-creative-group-${groupIndex}-asset-set-name`}
              style={{
                flex: 1, fontSize: 11.5, padding: "5px 8px",
                border: "1px solid var(--bo-line)", borderRadius: 4,
                background: "var(--bo-panel-alt)",
                color: "var(--bo-fg)",
              }}
              maxLength={120}
            />
            <button
              type="button"
              onClick={createSet}
              disabled={creating}
              className="bo-btn bo-btn-primary"
              data-testid={`camp-creative-group-${groupIndex}-asset-set-create`}
              style={{ fontSize: 11, padding: "4px 10px" }}
            >
              {creating ? "…" : "+ New set"}
            </button>
          </div>
          {loading && <div style={{ fontSize: 11, color: "var(--bo-muted)" }}>Loading…</div>}
          {sets.map((s) => (
            <AssetSetCard
              key={s.id}
              assetSet={s}
              groupIndex={groupIndex}
              textVersion={textVersion}
              imageVersions={imageVersions}
              backendUrl={backendUrl}
              onChanged={reload}
            />
          ))}
          {!loading && sets.length === 0 && (
            <div style={{ fontSize: 11, color: "var(--bo-muted)" }}>
              No asset sets yet. Create one to bundle a text variant + image variant for review.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function AssetSetCard({ assetSet, groupIndex, textVersion, imageVersions, backendUrl, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [picking, setPicking] = useState(false);
  const frozen = assetSet.status === "approved" || assetSet.status === "published";

  const patchPicks = async (picks) => {
    setBusy(true);
    try {
      await api.patch(`/creative/asset-sets/${assetSet.id}`, picks);
      toast.success("Picks updated");
      await onChanged();
    } catch (e) {
      toast.error("Update refused", { description: apiErr(e) });
    } finally {
      setBusy(false);
    }
  };

  const advance = async () => {
    const to = NEXT_STATUS[assetSet.status];
    if (!to) return;
    setBusy(true);
    try {
      await api.post(`/creative/asset-sets/${assetSet.id}/status`, { status: to });
      toast.success(`Moved to ${to.replace("_", " ")}`);
      await onChanged();
    } catch (e) {
      toast.error("Transition refused", { description: apiErr(e) });
    } finally {
      setBusy(false);
    }
  };

  const textPicked = Boolean(assetSet.text_variant_id);
  const imagePicked = Boolean(assetSet.image_variant_id);

  return (
    <div
      data-testid={`camp-creative-group-${groupIndex}-asset-set-${assetSet.id}`}
      style={{
        border: frozen ? "1px solid var(--bo-accent, #b98a4a)" : "1px solid var(--bo-line)",
        borderRadius: 6, padding: 10,
        background: frozen ? "rgba(185,138,74,0.06)" : "var(--bo-panel-alt)",
      }}
    >
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <span style={{ fontSize: 12, fontWeight: 600 }}>{assetSet.name}</span>
        <span
          data-testid={`camp-creative-group-${groupIndex}-asset-set-${assetSet.id}-status`}
          style={{
            fontSize: 10, padding: "2px 7px", borderRadius: 3,
            fontFamily: "var(--bo-mono)", letterSpacing: 0.5,
            background: frozen ? "var(--bo-accent, #b98a4a)" : "var(--bo-panel)",
            color: frozen ? "#fff" : "var(--bo-fg)",
            border: "1px solid var(--bo-line)",
          }}
        >
          {STATUS_LABEL[assetSet.status] || assetSet.status}
        </span>
        <span style={{ fontSize: 10.5, color: "var(--bo-muted)", fontFamily: "var(--bo-mono)" }}>
          text {textPicked ? "✓" : "—"} · image {imagePicked ? "✓" : "—"}
        </span>
        {NEXT_STATUS[assetSet.status] && (
          <button
            type="button"
            onClick={advance}
            disabled={busy}
            className="bo-btn"
            data-testid={`camp-creative-group-${groupIndex}-asset-set-${assetSet.id}-advance`}
            style={{ marginLeft: "auto", fontSize: 11, padding: "3px 8px" }}
          >
            {busy ? "…" : `Move to ${NEXT_STATUS[assetSet.status].replace("_", " ")}`}
          </button>
        )}
      </div>
      {!frozen && (
        <div style={{ marginTop: 8, display: "flex", gap: 6, flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={() => setPicking((p) => !p)}
            className="bo-btn"
            data-testid={`camp-creative-group-${groupIndex}-asset-set-${assetSet.id}-pick-btn`}
            style={{ fontSize: 10, padding: "2px 8px" }}
          >
            {picking ? "Hide picks" : "Pick variants"}
          </button>
        </div>
      )}
      {picking && !frozen && (
        <div
          style={{
            marginTop: 8,
            padding: 6,
            border: "1px dashed var(--bo-line)",
            borderRadius: 4,
            background: "var(--bo-panel)",
            display: "grid",
            gap: 8,
          }}
        >
          {/* Text pick */}
          <div>
            <div className="bo-eyebrow" style={{ fontSize: 9, marginBottom: 3 }}>
              Text variant
            </div>
            <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
              {textVersion?.variants?.map((v) => (
                <button
                  key={v.id}
                  type="button"
                  className="bo-btn"
                  onClick={() => patchPicks({
                    text_version_id: textVersion.id,
                    text_variant_id: v.id,
                  })}
                  aria-pressed={assetSet.text_variant_id === v.id}
                  data-testid={`camp-creative-group-${groupIndex}-asset-set-${assetSet.id}-pick-text-${v.id}`}
                  style={{
                    fontSize: 10, padding: "2px 6px",
                    background: assetSet.text_variant_id === v.id
                      ? "var(--bo-accent, #b98a4a)"
                      : "transparent",
                    color: assetSet.text_variant_id === v.id ? "#fff" : "var(--bo-fg)",
                  }}
                >
                  #{(v.index ?? 0) + 1} {v.headline ? "· " + v.headline.slice(0, 24) : ""}
                </button>
              )) || <span style={{ fontSize: 10, color: "var(--bo-muted)" }}>No text variants yet</span>}
            </div>
          </div>
          {/* Image pick */}
          <div>
            <div className="bo-eyebrow" style={{ fontSize: 9, marginBottom: 3 }}>
              Image variant
            </div>
            <div style={{
              display: "grid", gap: 4,
              gridTemplateColumns: "repeat(auto-fill, minmax(80px, 1fr))",
            }}>
              {(imageVersions || []).flatMap((iv) => (iv.variants || []).map((v) => (
                <button
                  key={v.id}
                  type="button"
                  className="bo-btn"
                  onClick={() => patchPicks({
                    image_version_id: iv.id,
                    image_variant_id: v.id,
                  })}
                  aria-pressed={assetSet.image_variant_id === v.id}
                  data-testid={`camp-creative-group-${groupIndex}-asset-set-${assetSet.id}-pick-img-${v.id}`}
                  style={{
                    padding: 0,
                    border: assetSet.image_variant_id === v.id
                      ? "2px solid var(--bo-accent, #b98a4a)"
                      : "1px solid var(--bo-line)",
                    borderRadius: 4, overflow: "hidden",
                    aspectRatio: v.aspect_ratio?.replace(":", " / ") || "1 / 1",
                    background: "#111",
                  }}
                >
                  <img
                    src={`${backendUrl}/api/brand-os/creative/images/${iv.id}/${v.id}`}
                    alt={v.alt_text || "variant"}
                    style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                  />
                </button>
              )))}
              {(!imageVersions || imageVersions.length === 0) && (
                <span style={{ fontSize: 10, color: "var(--bo-muted)" }}>
                  No image variants yet
                </span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
